import { useQuery } from '@tanstack/react-query';
import type {
  MetricsOverviewDto,
  MetricsRange,
  ResourceMetricsDto,
  SubscriptionMetricsDto,
} from '@cpm/shared';
import { Gauge, Table2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  formatMb,
  formatPercent,
  MetricsTable,
  RangeTabs,
  StatTile,
  timeAgo,
  UsageChart,
} from './charts.js';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorNote, Spinner } from './ui.js';

/**
 * Usage panels (ADR-0012) for the resource, subscription and dashboard
 * pages. Availability is rendered honestly: resources Sentinel cannot
 * measure (service templates, docker-compose apps) say so explicitly and
 * never chart as zeros.
 */

function UnavailableNotice({ reason }: { reason: string | null }) {
  return (
    <EmptyState
      icon={<Gauge className="size-10" />}
      title="Metrics unavailable"
      description={reason ?? 'Sentinel does not collect metrics for this resource.'}
    />
  );
}

function PendingNotice({ reason }: { reason: string | null }) {
  return (
    <EmptyState
      icon={<Gauge className="size-10" />}
      title="No metrics yet"
      description={reason ?? 'Waiting for the first collection run.'}
      action={
        <Link
          to="/infrastructure"
          className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Check Sentinel status
        </Link>
      }
    />
  );
}

function ChartHeaderActions({
  range,
  onRange,
  table,
  onTable,
}: {
  range: MetricsRange;
  onRange: (r: MetricsRange) => void;
  table: boolean;
  onTable: () => void;
}) {
  return (
    <>
      <RangeTabs value={range} onChange={onRange} />
      <Button
        variant="ghost"
        onClick={onTable}
        aria-pressed={table}
        title={table ? 'Show charts' : 'Show data table'}
      >
        <Table2 className="size-4" />
      </Button>
    </>
  );
}

/** CPU/memory usage card for application and database detail pages. */
export function ResourceMetricsPanel({ resourceId }: { resourceId: string }) {
  const [range, setRange] = useState<MetricsRange>('24h');
  const [table, setTable] = useState(false);
  const metrics = useQuery({
    queryKey: ['metrics', 'resource', resourceId, range],
    queryFn: () => api.get<ResourceMetricsDto>(`/api/metrics/resources/${resourceId}?range=${range}`),
    refetchInterval: 60_000,
  });

  return (
    <Card>
      <CardHeader
        title="Usage"
        description="CPU and memory from Sentinel, hourly averages with peak band"
        actions={
          metrics.data?.availability === 'available' ? (
            <ChartHeaderActions
              range={range}
              onRange={setRange}
              table={table}
              onTable={() => setTable((t) => !t)}
            />
          ) : undefined
        }
      />
      {metrics.isPending ? (
        <Spinner />
      ) : metrics.error ? (
        <div className="p-5">
          <ErrorNote error={metrics.error} />
        </div>
      ) : metrics.data.availability === 'unsupported' ? (
        <UnavailableNotice reason={metrics.data.reason} />
      ) : metrics.data.availability === 'pending' ? (
        <PendingNotice reason={metrics.data.reason} />
      ) : (
        <div className="space-y-4 px-5 py-4">
          {metrics.data.latest && (
            <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
              <StatTile
                label="CPU now"
                value={formatPercent(metrics.data.latest.cpuPercent)}
                hint={`sampled ${timeAgo(metrics.data.latest.sampledAt)}`}
              />
              <StatTile label="Memory now" value={formatMb(metrics.data.latest.memoryMb)} />
            </div>
          )}
          {table ? (
            <MetricsTable points={metrics.data.series} range={range} />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <UsageChart kind="cpu" title="CPU" points={metrics.data.series} range={range} />
              <UsageChart kind="memory" title="Memory" points={metrics.data.series} range={range} />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Aggregated usage card for the subscription detail page. */
export function SubscriptionMetricsPanel({ subscriptionId }: { subscriptionId: string }) {
  const [range, setRange] = useState<MetricsRange>('24h');
  const [table, setTable] = useState(false);
  const metrics = useQuery({
    queryKey: ['metrics', 'subscription', subscriptionId, range],
    queryFn: () =>
      api.get<SubscriptionMetricsDto>(`/api/metrics/subscriptions/${subscriptionId}?range=${range}`),
    refetchInterval: 60_000,
  });

  if (metrics.isPending) {
    return (
      <Card>
        <CardHeader title="Usage" />
        <Spinner />
      </Card>
    );
  }
  if (metrics.error) {
    // The metrics module can be disabled — hide the card entirely then.
    return null;
  }
  const data = metrics.data;
  const excluded = data.resources.filter((r) => r.availability !== 'available');
  const hasCharts = data.resources.some((r) => r.availability === 'available');

  return (
    <Card>
      <CardHeader
        title="Usage"
        description="Summed across this subscription's measurable resources"
        actions={
          hasCharts ? (
            <ChartHeaderActions
              range={range}
              onRange={setRange}
              table={table}
              onTable={() => setTable((t) => !t)}
            />
          ) : undefined
        }
      />
      {!hasCharts ? (
        data.resources.length === 0 ? (
          <EmptyState
            icon={<Gauge className="size-10" />}
            title="No resources to measure"
            description="Usage charts appear once this subscription has applications or databases."
          />
        ) : (
          <UnavailableNotice
            reason={excluded[0]?.reason ?? 'Sentinel cannot measure these resources.'}
          />
        )
      ) : (
        <div className="space-y-4 px-5 py-4">
          {table ? (
            <MetricsTable points={data.series} range={range} />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <UsageChart
                kind="cpu"
                title="CPU (all resources)"
                points={data.series}
                range={range}
                peakLabel="combined peak"
              />
              <UsageChart
                kind="memory"
                title="Memory (all resources)"
                points={data.series}
                range={range}
                peakLabel="combined peak"
              />
            </div>
          )}
          {excluded.length > 0 && (
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span>Not measurable:</span>
              {excluded.map((r) => (
                <span key={r.resourceId} title={r.reason ?? undefined}>
                  <Badge tone="gray">
                    {r.name} — {r.availability === 'unsupported' ? 'unsupported' : 'no data yet'}
                  </Badge>
                </span>
              ))}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/** Dashboard card: environment-wide usage + per-server system load. */
export function DashboardMetricsPanel() {
  const [range, setRange] = useState<MetricsRange>('24h');
  const metrics = useQuery({
    queryKey: ['metrics', 'overview', range],
    queryFn: () => api.get<MetricsOverviewDto>(`/api/metrics/overview?range=${range}`),
    refetchInterval: 60_000,
  });

  // Metrics module disabled or nothing collected → keep the dashboard calm.
  if (metrics.isPending || metrics.error) return null;
  const data = metrics.data;
  const empty = data.resources.length === 0 && data.servers.length === 0;

  return (
    <div className="mt-6">
      <Card>
        <CardHeader
          title="Usage"
          description="Managed resources and server load, from Sentinel"
          actions={<RangeTabs value={range} onChange={setRange} />}
        />
        {empty ? (
          <PendingNotice reason="No metrics collected yet — enable Sentinel metrics per server and configure the panel's SSH key." />
        ) : (
          <div className="space-y-6 px-5 py-4">
            {data.resources.length > 0 && (
              <div className="grid gap-6 lg:grid-cols-2">
                <UsageChart
                  kind="cpu"
                  title="CPU — all managed resources"
                  points={data.resources}
                  range={range}
                  peakLabel="combined peak"
                />
                <UsageChart
                  kind="memory"
                  title="Memory — all managed resources"
                  points={data.resources}
                  range={range}
                  peakLabel="combined peak"
                />
              </div>
            )}
            {data.servers.map((server) => (
              <div key={server.serverUuid}>
                <p className="mb-2 text-sm font-medium">
                  Server load — {server.serverName}
                </p>
                <div className="grid gap-6 lg:grid-cols-2">
                  <UsageChart
                    kind="cpu"
                    title="System CPU"
                    points={server.series}
                    range={range}
                  />
                  <UsageChart
                    kind="memory"
                    title="System memory"
                    points={server.series}
                    range={range}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
